export const INBOX_TYPES = {
  WEB: 'Channel::WebWidget',
  FB: 'Channel::FacebookPage',
  TWITTER: 'Channel::TwitterProfile',
  TWILIO: 'Channel::TwilioSms',
  WHATSAPP: 'Channel::Whatsapp',
  API: 'Channel::Api',
  EMAIL: 'Channel::Email',
  TELEGRAM: 'Channel::Telegram',
  LINE: 'Channel::Line',
  SMS: 'Channel::Sms',
  INSTAGRAM: 'Channel::Instagram',
  TIKTOK: 'Channel::Tiktok',
  VOICE: 'Channel::Voice',
  MAX: 'Channel::Max',
  MAX_BOT: 'Channel::MaxBot',
  TELEGRAM_USER: 'Channel::TelegramUser',
  VK: 'Channel::Vk',
  UIS: 'Channel::Uis',
  MANGO: 'Channel::Mango',
  MEGAPBX: 'Channel::Megapbx',
};

// Add providers here as they gain voice capability (e.g., WhatsApp Cloud, Twilio WhatsApp)
export const VOICE_CALL_PROVIDERS = {
  TWILIO: 'twilio',
  WHATSAPP: 'whatsapp',
  UIS: 'uis',
  MANGO: 'mango',
  MEGAPBX: 'megapbx',
};

export const getVoiceCallProvider = inbox => {
  if (!inbox) return null;

  // Callers pass either snake_case (raw API) or camelCase (after camelcaseKeys) shapes.
  const channelType = inbox.channel_type || inbox.channelType;
  const voiceEnabled = inbox.voice_enabled || inbox.voiceEnabled;

  if (!voiceEnabled) return null;

  if (channelType === INBOX_TYPES.TWILIO) return VOICE_CALL_PROVIDERS.TWILIO;
  if (channelType === INBOX_TYPES.WHATSAPP)
    return VOICE_CALL_PROVIDERS.WHATSAPP;
  // MANGO exposes the same click-to-call button; the audio itself rings on the
  // agent's own device (CTI), so no in-browser call UI is started.
  if (channelType === INBOX_TYPES.MANGO) return VOICE_CALL_PROVIDERS.MANGO;
  // МегаФон ВАТС offers click-to-call (makecall) and a native-WSS browser
  // softphone; audio runs through the vivaPhone dock, not the Twilio widget.
  if (channelType === INBOX_TYPES.MEGAPBX) return VOICE_CALL_PROVIDERS.MEGAPBX;
  // UIS the same: click-to-call is server-side (start.employee_call), so the
  // «Call» button belongs here even though the audio never touches the widget.
  if (channelType === INBOX_TYPES.UIS) return VOICE_CALL_PROVIDERS.UIS;

  return null;
};

// АТС-провайдеры: звук в доке vivaPhone или на устройстве оператора, не в
// Twilio-виджете.
export const PBX_PROVIDERS = [
  VOICE_CALL_PROVIDERS.UIS,
  VOICE_CALL_PROVIDERS.MANGO,
  VOICE_CALL_PROVIDERS.MEGAPBX,
];

// Server-initiated исходящий, где АТС сначала звонит на линию оператора:
// софтфон снимает этот лег сам.
export const isAutoAnswerProvider = provider =>
  provider === VOICE_CALL_PROVIDERS.UIS ||
  provider === VOICE_CALL_PROVIDERS.MANGO;

export const isVoiceCallEnabled = inbox => getVoiceCallProvider(inbox) !== null;

// Источники, где ЛЮБОЙ диалог — это звонок: текст клиенту туда не уходит
// вообще (зеркало Inbox#voice_only? на бэке). В таком диалоге оператору
// доступны только звонок и заметки.
export const VOICE_ONLY_CHANNELS = [
  INBOX_TYPES.UIS,
  INBOX_TYPES.MANGO,
  INBOX_TYPES.MEGAPBX,
];

export const isVoiceOnlyInbox = inbox =>
  VOICE_ONLY_CHANNELS.includes(inbox?.channel_type || inbox?.channelType);

// Инбокс, где звук НЕ в браузере: АТС звонит на устройство оператора
// (стационарный/мобильный), софтфон не поднимается, кнопок «принять/сбросить»
// нет — только карточка «кто звонит» (provider_config.audio_mode = cti).
export const isDeviceAudioInbox = inbox =>
  (inbox?.device_audio ?? inbox?.deviceAudio) === true;

export const TWILIO_CHANNEL_MEDIUM = {
  WHATSAPP: 'whatsapp',
  SMS: 'sms',
};

const INBOX_ICON_MAP_FILL = {
  [INBOX_TYPES.WEB]: 'i-ri-global-fill',
  [INBOX_TYPES.FB]: 'i-ri-messenger-fill',
  [INBOX_TYPES.TWITTER]: 'i-ri-twitter-x-fill',
  [INBOX_TYPES.WHATSAPP]: 'i-ri-whatsapp-fill',
  [INBOX_TYPES.API]: 'i-ri-cloudy-fill',
  [INBOX_TYPES.EMAIL]: 'i-ri-mail-fill',
  [INBOX_TYPES.TELEGRAM]: 'i-ri-telegram-fill',
  [INBOX_TYPES.LINE]: 'i-ri-line-fill',
  [INBOX_TYPES.INSTAGRAM]: 'i-ri-instagram-fill',
  [INBOX_TYPES.TIKTOK]: 'i-ri-tiktok-fill',
  [INBOX_TYPES.VOICE]: 'i-ri-phone-fill',
  [INBOX_TYPES.MAX]: 'i-ri-chat-smile-3-fill',
  [INBOX_TYPES.MAX_BOT]: 'i-ri-chat-smile-3-fill',
  [INBOX_TYPES.TELEGRAM_USER]: 'i-ri-telegram-fill',
  [INBOX_TYPES.VK]: 'i-ri-chat-smile-3-fill',
  [INBOX_TYPES.UIS]: 'i-ri-phone-fill',
  [INBOX_TYPES.MANGO]: 'i-ri-phone-fill',
  [INBOX_TYPES.MEGAPBX]: 'i-ri-phone-fill',
};

const DEFAULT_ICON_FILL = 'i-ri-chat-1-fill';

const INBOX_ICON_MAP_LINE = {
  [INBOX_TYPES.WEB]: 'i-woot-website',
  [INBOX_TYPES.FB]: 'i-woot-messenger',
  [INBOX_TYPES.TWITTER]: 'i-woot-x',
  [INBOX_TYPES.WHATSAPP]: 'i-woot-whatsapp',
  [INBOX_TYPES.API]: 'i-woot-api',
  [INBOX_TYPES.EMAIL]: 'i-woot-mail',
  [INBOX_TYPES.TELEGRAM]: 'i-woot-telegram',
  [INBOX_TYPES.LINE]: 'i-woot-line',
  [INBOX_TYPES.INSTAGRAM]: 'i-woot-instagram',
  [INBOX_TYPES.TIKTOK]: 'i-woot-tiktok',
};

const DEFAULT_ICON_LINE = 'i-ri-chat-1-line';

export const getInboxSource = (type, phoneNumber, inbox) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return inbox.website_url || '';

    case INBOX_TYPES.TWILIO:
    case INBOX_TYPES.WHATSAPP:
      return phoneNumber || '';

    case INBOX_TYPES.EMAIL:
      return inbox.email || '';

    default:
      return '';
  }
};
export const getReadableInboxByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'livechat';

    case INBOX_TYPES.FB:
      return 'facebook';

    case INBOX_TYPES.TWITTER:
      return 'twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp') ? 'whatsapp' : 'sms';

    case INBOX_TYPES.WHATSAPP:
      return 'whatsapp';

    case INBOX_TYPES.API:
      return 'api';

    case INBOX_TYPES.EMAIL:
      return 'email';

    case INBOX_TYPES.TELEGRAM:
      return 'telegram';

    case INBOX_TYPES.LINE:
      return 'line';

    default:
      return 'chat';
  }
};

export const getInboxClassByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'globe-desktop';

    case INBOX_TYPES.FB:
      return 'brand-facebook';

    case INBOX_TYPES.TWITTER:
      return 'brand-twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp')
        ? 'brand-whatsapp'
        : 'brand-sms';

    case INBOX_TYPES.WHATSAPP:
      return 'brand-whatsapp';

    case INBOX_TYPES.API:
      return 'cloud';

    case INBOX_TYPES.EMAIL:
      return 'mail';

    case INBOX_TYPES.TELEGRAM:
      return 'brand-telegram';

    case INBOX_TYPES.LINE:
      return 'brand-line';

    case INBOX_TYPES.INSTAGRAM:
      return 'brand-instagram';

    case INBOX_TYPES.TIKTOK:
      return 'brand-tiktok';

    default:
      return 'chat';
  }
};

export const getInboxIconByType = (type, medium, variant = 'fill') => {
  const iconMap =
    variant === 'fill' ? INBOX_ICON_MAP_FILL : INBOX_ICON_MAP_LINE;
  const defaultIcon =
    variant === 'fill' ? DEFAULT_ICON_FILL : DEFAULT_ICON_LINE;

  // Special case for Twilio (whatsapp and sms)
  if (type === INBOX_TYPES.TWILIO && medium === 'whatsapp') {
    return iconMap[INBOX_TYPES.WHATSAPP];
  }

  return iconMap[type] ?? defaultIcon;
};

export const getInboxWarningIconClass = (type, reauthorizationRequired) => {
  const allowedInboxTypes = [INBOX_TYPES.FB, INBOX_TYPES.EMAIL];
  if (allowedInboxTypes.includes(type) && reauthorizationRequired) {
    return 'warning';
  }
  return '';
};
